import { useEffect, useRef, useState } from "react";

/**
 * Header group switcher — a chevron toggle next to the logo that opens a
 * searchable list of the member's joined groups. Picking one jumps to that
 * group's feed; "Discover groups" returns to the community group collection.
 * (Skool-style community switcher, adapted to this app's groups.)
 */

function Thumb({ g, primary }) {
  const initial = (g.name || "?").charAt(0).toUpperCase();
  return (
    <span
      className="w-8 h-8 rounded-lg overflow-hidden shrink-0 flex items-center justify-center text-white text-sm font-bold"
      style={{ backgroundColor: primary }}
    >
      {g.banner ? (
        <img src={g.banner} alt="" className="w-full h-full object-cover" onError={(e) => (e.currentTarget.style.display = "none")} />
      ) : (
        initial
      )}
    </span>
  );
}

export default function GroupSwitcher({ groups = [], currentToken, primary = "#0274F7", onSelect, onDiscover }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const ref = useRef(null);

  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const needle = q.trim().toLowerCase();
  const list = groups.filter((g) => !needle || (g.name || "").toLowerCase().includes(needle));

  const pick = (g) => {
    setOpen(false);
    setQ("");
    if (g.group_token && g.group_token !== currentToken) onSelect?.(g.group_token);
  };

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-label="Switch group"
        className="w-8 h-8 flex items-center justify-center rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50 hover:text-gray-700 transition"
        style={open ? { borderColor: primary, color: primary } : undefined}
      >
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
          <path strokeLinecap="round" strokeLinejoin="round" d="M8 9l4-4 4 4M16 15l-4 4-4-4" />
        </svg>
      </button>

      {open && (
        <div className="absolute left-0 mt-2 w-72 z-[2000] bg-white rounded-2xl shadow-xl ring-1 ring-black/5 border border-gray-100 overflow-hidden max-sm:fixed max-sm:left-3 max-sm:right-3 max-sm:top-[68px] max-sm:mt-0 max-sm:w-auto">
          {/* Search */}
          <div className="p-2.5 border-b border-gray-100">
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35M11 19a8 8 0 100-16 8 8 0 000 16z" />
                </svg>
              </span>
              <input
                autoFocus
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search"
                className="w-full rounded-lg bg-gray-100 pl-9 pr-3 py-2 text-sm text-gray-800 outline-none focus:bg-gray-50 focus:ring-2"
                style={{ "--tw-ring-color": `${primary}33` }}
              />
            </div>
          </div>

          {/* Joined groups */}
          <div className="max-h-72 overflow-y-auto py-1.5">
            <p className="px-3 pt-1 pb-1.5 text-[11px] font-semibold uppercase tracking-wider text-gray-400">Your groups</p>
            {list.length === 0 ? (
              <p className="px-3 py-3 text-sm text-gray-400">No groups found.</p>
            ) : (
              list.map((g) => {
                const active = g.group_token === currentToken;
                return (
                  <button
                    key={g.group_token}
                    type="button"
                    onClick={() => pick(g)}
                    className="w-full flex items-center gap-2.5 px-3 py-2 hover:bg-gray-50 transition text-left"
                    style={active ? { backgroundColor: `${primary}0d` } : undefined}
                  >
                    <Thumb g={g} primary={primary} />
                    <span className="flex-1 min-w-0 font-medium text-gray-800 text-sm truncate">{g.name}</span>
                    {active && (
                      <svg className="w-4 h-4 shrink-0" viewBox="0 0 24 24" fill="none" stroke={primary} strokeWidth="2.5">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                  </button>
                );
              })
            )}
          </div>

          {/* Discover */}
          <div className="border-t border-gray-100 py-1.5">
            <button
              type="button"
              onClick={() => { setOpen(false); onDiscover?.(); }}
              className="w-full flex items-center gap-2.5 px-3 py-2 hover:bg-gray-50 transition text-left"
            >
              <span className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center text-gray-500 shrink-0">
                <svg className="w-[18px] h-[18px]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
                  <circle cx="12" cy="12" r="9" />
                  <path strokeLinecap="round" strokeLinejoin="round" d="M14.5 9.5l-1.6 4-4 1.6 1.6-4 4-1.6z" />
                </svg>
              </span>
              <span className="font-semibold text-gray-800 text-sm">Discover groups</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
